import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import axiosInstance from "./axios";
import { Warehouse } from "@/schemas/store";
import { KratosNode } from "./types/kratos";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Helper function to build query string
/* eslint-disable @typescript-eslint/no-explicit-any */
export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

export async function createFileFromUrl(
  url: string,
  filename?: string,
): Promise<File> {
  const response = await fetch(url);
  const blob = await response.blob();

  // Extract filename from URL if not provided
  if (!filename) {
    filename = url.split("/").pop() || "download";
  }

  return new File([blob], filename, {
    type: blob.type,
    lastModified: Date.now(),
  });
}

export const downloadInvoice = async (id: string) => {
  try {
    const response = await axiosInstance.post(
      `/order/order/invoice?id=${id}`,
      {},
      {
        responseType: "blob",
        baseURL: "/api/proxy",
      },
    );
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers["content-disposition"];
    const filename = contentDisposition
      ? contentDisposition.split("filename=")[1].replace(/"/g, "")
      : `invoice-${id}.pdf`;

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Invoice download failed:", error);
    throw error;
  }
};

export function formatWarehouseAddress(
  warehouseData: Warehouse,
  warehouseCode: string,
): string {
  const {
    province,
    city,
    district,
    streetAddress,
    attendantName,
    contactNumber,
  } = warehouseData;

  const addressLines = [`Name: ${attendantName} ${warehouseCode}`];

  if (contactNumber) {
    addressLines.push(`Contact Number: ${contactNumber}`);
  }

  addressLines.push(
    `Address: ${province} ${city} ${district} ${streetAddress} ${warehouseCode}`,
  );

  return addressLines.join("\n").trim();
}

export function formatAddressForForms(
  warehouseData: Warehouse,
  warehouseCode: string,
): string {
  const {
    province,
    city,
    district,
    streetAddress,
    attendantName,
    contactNumber,
  } = warehouseData;

  const parts = [`Name: ${attendantName}${warehouseCode}`];

  if (contactNumber) {
    parts.push(`Contact Number: ${contactNumber}`);
  }

  parts.push(
    `Province: ${province}`,
    `City: ${city}`,
    `District: ${district}`,
    `Zip code: 510000`,
    `Full Address: ${province} ${city} ${district} ${streetAddress}${warehouseCode}`,
  );

  return parts.join("\n").trim();
}


interface ToastInterface {
  error: (message: string, options?: { id: string | number }) => void;
}

/**
 * Parses nested Kratos UI errors (like weak passwords or validation issues)
 * and displays the specific error text in a toast notification.
 */
export function handleKratosFormErrors(
  kratosPayload: any, 
  toast: ToastInterface, 
  toastId: string | number
): void {
  let nodeErrorMessage: string | undefined;

  const nodes = (kratosPayload?.ui?.nodes as KratosNode[]) || [];
  for (const node of nodes) {
    const errorMsg = node.messages?.[0]?.text;
    if (errorMsg) {
      nodeErrorMessage = errorMsg;
      break;
    }
  }

  const generalErrorMsg = kratosPayload?.ui?.messages?.[0]?.text;

  if (nodeErrorMessage || generalErrorMsg) {
    toast.error(nodeErrorMessage || generalErrorMsg, { id: toastId });
  } else {
    toast.error("Something went wrong. Please try again.", { id: toastId });
  }
}